import type { CategoryLeaf } from '@repo/api-contracts';
import { GripVertical } from 'lucide-react';
import type { ReactElement } from 'react';
import { beginMoveDrag, type RenameController } from '@/shared/lib';
import { cn, DropdownMenu } from '@/shared/ui';
import type { DragReorderController } from '../model/drag-reorder-controller';
import { CategoryNameCell } from './category-name-cell';

interface CategoryChildChipProps {
  readonly child: CategoryLeaf;
  readonly index: number;
  readonly drag: DragReorderController;
  readonly rename: RenameController;
  readonly onRenameStart: (category: CategoryLeaf) => void;
  readonly onDelete: (category: CategoryLeaf) => void;
}

/** 소분류 칩 — 드래그로 순서를 바꾸고, 수정·삭제는 미트볼 메뉴 안에 있다. */
export const CategoryChildChip = ({
  child,
  index,
  drag,
  rename,
  onRenameStart,
  onDelete,
}: CategoryChildChipProps): ReactElement => {
  const isRenaming = rename.target?.id === child.id;

  return (
    <li
      draggable={!isRenaming}
      onDragStart={(event) => {
        if (beginMoveDrag(event, child.id)) {
          drag.onDragStart(index);
        }
      }}
      onDragOver={(event) => event.preventDefault()}
      onDrop={() => drag.onDrop(index)}
      onDragEnd={drag.onDragEnd}
      className={cn(
        'flex cursor-grab items-center gap-1 rounded-full border bg-background py-1 pl-2 pr-1 text-sm',
        drag.draggingIndex === index && 'opacity-40',
      )}
    >
      <GripVertical className="size-3.5 text-muted-foreground" aria-hidden="true" />
      <CategoryNameCell category={child} rename={rename} />
      {isRenaming ? null : (
        <DropdownMenu
          ariaLabel={`${child.name} 메뉴`}
          items={[
            { label: '이름 수정', onSelect: () => onRenameStart(child) },
            { label: '삭제', isDestructive: true, onSelect: () => onDelete(child) },
          ]}
        />
      )}
    </li>
  );
};
